import { Workspace, WorkspaceUsage } from "../../types/workspace";
import { RapidStateCreator } from "../types";

export interface WorkspaceSliceState {
    workspacesLoading: boolean;
    workspacesError: string | null;
    workspaces: Workspace[] | undefined;
    workspacesHasAPIFetched: boolean;
    workspaceUsages: WorkspaceUsage[];
    currentWorkspace: Workspace | null;
    apiKeys: Record<string, string>; // Map of workspace ID to API key

    // Getters
    getCurrentWorkspace: () => Workspace | null;
    getCurrentWorkspaceUsage: () => WorkspaceUsage | undefined;
    getWorkspaceById: (id: string) => Workspace | undefined;
    getWorkspaceUsageById: (id: string) => WorkspaceUsage | undefined;
    getApiKeyForWorkspace: (workspaceId: string) => string | null;
    getCurrentWorkspaceApiKey: () => string | null;

    // Setters
    setWorkspaces: (workspaces: Workspace[]) => void;
    setworkspacesHasAPIFetched: (workspacesHasAPIFetched: boolean) => void;
    setWorkspaceUsages: (usages: WorkspaceUsage[]) => void;
    setCurrentWorkspace: (workspace: Workspace | null) => void;
    setApiKey: (workspaceId: string, apiKey: string) => void;
    setWorkspacesLoading: (workspacesLoading: boolean) => void;
    setWorkspacesError: (workspacesError: string | null) => void;

    // Actions
    switchWorkspace: (workspace: Workspace, api?: any, onSuccess?: () => void) => void;
    addWorkspace: (workspace: Workspace) => void;
    addWorkspaceUsage: (usage: WorkspaceUsage) => void;
    updateWorkspaces: (workspaces: Workspace[], usages: WorkspaceUsage[]) => void;
    clearWorkspaceState: () => void;

    // API Actions
    fetchWorkspaces: (
        api: any,
        user: any,
        workspaceUrl: string | null,
        onSuccess?: (currentWorkspace: Workspace | null) => void,
        onError?: (e: Error) => void
    ) => Promise<void>;
    fetchApiKeyForWorkspace: (workspaceId: string, api: any) => Promise<void>;
    fetchCurrentWorkspaceApiKey: (api: any) => Promise<void>;

    // Legacy persistence helpers (can be removed since using Zustand persist middleware)
    persistWorkspace: (workspace: Workspace | null) => void;
    loadPersistedWorkspace: () => { workspaceId: string | null; workspaceUrl: string | null };
}

export const createWorkspaceSlice: RapidStateCreator<WorkspaceSliceState> = (set, get) => ({
    // Initial state
    workspacesLoading: false,
    workspacesError: null,
    workspaces: undefined,
    workspaceUsages: [],
    workspacesHasAPIFetched: false,
    currentWorkspace: null,
    apiKeys: {} as Record<string, string>,

    // Getters
    getCurrentWorkspace: () => get().currentWorkspace,

    getCurrentWorkspaceUsage: () => {
        const currentWorkspace = get().currentWorkspace;
        if (!currentWorkspace) return undefined;

        return get().workspaceUsages.find(
            (usage: WorkspaceUsage) => usage.id === currentWorkspace.id
        );
    },

    getWorkspaceById: (id: string) => {
        return get().workspaces?.find((workspace: Workspace) => workspace.id === id);
    },

    getWorkspaceUsageById: (id: string) => {
        return get().workspaceUsages.find((usage: WorkspaceUsage) => usage.id === id);
    },

    getApiKeyForWorkspace: (workspaceId: string) => {
        return get().apiKeys[workspaceId] || null;
    },

    getCurrentWorkspaceApiKey: () => {
        const currentWorkspace = get().currentWorkspace;
        if (!currentWorkspace || !currentWorkspace.id) return null;
        return get().apiKeys[currentWorkspace.id] || null;
    },

    // Setters
    setWorkspaces: (workspaces: Workspace[]) => set({ workspaces }, false, "setWorkspaces"),
    setworkspacesHasAPIFetched: (workspacesHasAPIFetched: boolean) =>
        set({ workspacesHasAPIFetched }, false, "setworkspacesHasAPIFetched"),
    setWorkspaceUsages: (workspaceUsages: WorkspaceUsage[]) =>
        set({ workspaceUsages }, false, "setWorkspaceUsages"),

    setCurrentWorkspace: (currentWorkspace: Workspace | null) => {
        // No need for manual persistence - Zustand persist middleware handles this
        set({ currentWorkspace }, false, "setCurrentWorkspace");

        // Auto-fetch API key for the workspace if not already cached
        // Note: This won't work on persistence restore since we don't have the api instance
        // but it will work when workspace is programmatically set
        if (currentWorkspace?.id && !get().apiKeys[currentWorkspace.id]) {
            // We can't fetch here since we don't have the api instance
            // The API key will be fetched in switchWorkspace or during workspace initialization
        }
    },

    setApiKey: (workspaceId: string, apiKey: string) => {
        const currentApiKeys = get().apiKeys;
        const newApiKeys = { ...currentApiKeys, [workspaceId]: apiKey };
        set({ apiKeys: newApiKeys }, false, "setApiKey");
    },

    setWorkspacesLoading: (workspacesLoading: boolean) =>
        set({ workspacesLoading }, false, "setWorkspacesLoading"),

    setWorkspacesError: (workspacesError: string | null) =>
        set({ workspacesError }, false, "setWorkspacesError"),

    // Actions
    switchWorkspace: (workspace: Workspace, api?: any, onSuccess?: () => void) => {
        // Update current workspace (persistence handled by Zustand middleware)
        set({ currentWorkspace: workspace }, false, "switchWorkspace");

        // Auto-fetch API key for the new workspace if not already cached
        if (api && workspace.id && !get().apiKeys[workspace.id]) {
            console.log(
                "🔑 [WorkspaceStore] Auto-fetching API key for switched workspace:",
                workspace.id
            );
            get().fetchApiKeyForWorkspace(workspace.id, api);
        } else if (workspace.id && get().apiKeys[workspace.id]) {
            console.log(
                "✅ [WorkspaceStore] API key already cached for switched workspace:",
                workspace.id
            );
        }

        if (onSuccess) onSuccess();
    },

    addWorkspace: (workspace: Workspace) => {
        const currentWorkspaces = get().workspaces || [];
        const existingIndex = currentWorkspaces.findIndex((w: Workspace) => w.id === workspace.id);

        let newWorkspaces;
        if (existingIndex >= 0) {
            // Update existing workspace
            newWorkspaces = [...currentWorkspaces];
            newWorkspaces[existingIndex] = workspace;
        } else {
            // Add new workspace
            newWorkspaces = [...currentWorkspaces, workspace];
        }

        set({ workspaces: newWorkspaces }, false, "addWorkspace");
    },

    addWorkspaceUsage: (usage: WorkspaceUsage) => {
        const currentUsages = get().workspaceUsages;
        const existingIndex = currentUsages.findIndex((u: WorkspaceUsage) => u.id === usage.id);

        let newUsages;
        if (existingIndex >= 0) {
            // Update existing usage
            newUsages = [...currentUsages];
            newUsages[existingIndex] = usage;
        } else {
            // Add new usage
            newUsages = [...currentUsages, usage];
        }

        set({ workspaceUsages: newUsages }, false, "addWorkspaceUsage");
    },

    updateWorkspaces: (workspaces: Workspace[], usages: WorkspaceUsage[]) => {
        set({ workspaces, workspaceUsages: usages }, false, "updateWorkspaces");
    },

    clearWorkspaceState: () => {
        // Clear all workspace state (persistence handled by Zustand middleware)
        set(
            {
                workspaces: [],
                workspaceUsages: [],
                currentWorkspace: null,
                workspacesHasAPIFetched: false,
                apiKeys: {},
                workspacesLoading: false,
                workspacesError: null
            },
            false,
            "clearWorkspaceState"
        );
    },

    // API Actions
    fetchWorkspaces: async (
        api: any,
        user: any,
        workspaceUrl: string | null,
        onSuccess?: (currentWorkspace: Workspace | null) => void,
        onError?: (e: Error) => void
    ) => {
        if (!user) {
            get().clearWorkspaceState();
            return;
        }

        try {
            set({ workspacesLoading: true, workspacesError: null }, false, "fetchWorkspaces:start");

            const loadMyWorkspacesPayload: { workspaceCreationName?: string } = {};
            if (user) {
                loadMyWorkspacesPayload.workspaceCreationName = `${user.displayName}'s Workspace`;
            }

            let {
                workspaces: workspacesFromApi,
                usages,
                created
            } = await api.workspace.loadMyWorkspaces(loadMyWorkspacesPayload);

            if (created) {
                const updatedUsage = await api.workspace.setupRapidCreatedWorkspace({
                    workspaceId: workspacesFromApi[0].id
                });
                usages = [updatedUsage];
            }

            let defaultWorkspace: Workspace | null = null;

            if (workspacesFromApi.length > 0) {
                // Update workspaces and usages
                set(
                    { workspaces: workspacesFromApi, workspaceUsages: usages },
                    false,
                    "fetchWorkspaces:success"
                );

                // Set default workspace (prefer saved, then personal, then first available)
                const currentWorkspace = get().currentWorkspace;

                if (workspaceUrl) {
                    defaultWorkspace = workspacesFromApi.find(
                        (w: Workspace) => w.url === workspaceUrl
                    );
                } else if (currentWorkspace) {
                    // Check if persisted workspace still exists
                    defaultWorkspace = workspacesFromApi.find(
                        (w: Workspace) => w.id === currentWorkspace.id
                    );
                }

                if (!defaultWorkspace) {
                    const personalWorkspace = workspacesFromApi.find(
                        (w: Workspace) => w.id === user?.uid
                    );
                    defaultWorkspace = personalWorkspace ?? workspacesFromApi[0];
                }

                if (defaultWorkspace) {
                    set(
                        { currentWorkspace: defaultWorkspace },
                        false,
                        "fetchWorkspaces:setDefault"
                    );

                    // Auto-fetch API key for the default workspace if not already cached
                    if (defaultWorkspace.id && !get().apiKeys[defaultWorkspace.id]) {
                        get().fetchApiKeyForWorkspace(defaultWorkspace.id, api);
                    }
                }
            } else {
                // No workspaces found
                set(
                    { workspaces: [], workspaceUsages: [], currentWorkspace: null },
                    false,
                    "fetchWorkspaces:empty"
                );
            }

            set(
                { workspacesLoading: false, workspacesHasAPIFetched: true },
                false,
                "fetchWorkspaces:success"
            );

            if (onSuccess) {
                onSuccess(defaultWorkspace);
            }
        } catch (error) {
            console.error("Error fetching workspaces:", error);
            const errorMessage =
                error instanceof Error ? error.message : "Failed to fetch workspaces";
            set(
                {
                    workspacesLoading: false,
                    workspacesError: errorMessage,
                    workspaces: [],
                    workspaceUsages: [],
                    currentWorkspace: null,
                    workspacesHasAPIFetched: false
                },
                false,
                "fetchWorkspaces:error"
            );
            if (onError) onError(new Error(errorMessage));
        } finally {
            set({ workspacesLoading: false }, false, "fetchWorkspaces:end");
        }
    },

    fetchApiKeyForWorkspace: async (workspaceId: string, api: any) => {
        try {
            const response = await api.workspace.getApiKey(workspaceId);
            if (response?.apiKey) {
                const currentApiKeys = get().apiKeys;
                const newApiKeys = { ...currentApiKeys, [workspaceId]: response.apiKey };
                set({ apiKeys: newApiKeys }, false, "fetchApiKeyForWorkspace:success");
            }
        } catch (error) {
            console.error("Error fetching API key for workspace:", workspaceId, error);
            // Don't set error state for API key fetch failures - it's not critical
        }
    },

    fetchCurrentWorkspaceApiKey: async (api: any) => {
        const currentWorkspace = get().currentWorkspace;
        if (currentWorkspace?.id && !get().apiKeys[currentWorkspace.id]) {
            await get().fetchApiKeyForWorkspace(currentWorkspace.id, api);
        }
    },

    // Legacy persistence helpers (no longer needed with Zustand persist middleware)
    persistWorkspace: (workspace: Workspace | null) => {
        // No-op: Zustand persist middleware handles this automatically
        console.warn(
            "persistWorkspace is deprecated - Zustand persist middleware handles this automatically"
        );
    },

    loadPersistedWorkspace: () => {
        // No-op: Zustand persist middleware handles this automatically
        console.warn(
            "loadPersistedWorkspace is deprecated - Zustand persist middleware handles this automatically"
        );
        return { workspaceId: null, workspaceUrl: null };
    }
});
